import React, { useState, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import { CodeSnippetViewer } from '../questions';
import { getLanguageFromSkills, languageDisplayNames } from '../utils/languageDetection';

function FullScreenCodeEditor({ 
  question, 
  value = '', 
  onChange, 
  disabled = false, 
  onClose,
  initialLanguage
}) {
  // Store the original auto-detected language for the code snippet (separate from user-selected language)
  const snippetLanguage = getLanguageFromSkills(question.skillsTested);
  
  // State for the selected language (for the solution editor)
  const [language, setLanguage] = useState(initialLanguage || getLanguageFromSkills(question.skillsTested));
  
  // Update language when question ID or initialLanguage changes
  useEffect(() => {
    if (initialLanguage) {
      setLanguage(initialLanguage);
    } else {
      setLanguage(getLanguageFromSkills(question.skillsTested));
    }
  }, [question.id, initialLanguage]);
  
  // Handle language change
  const handleLanguageChange = (e) => {
    setLanguage(e.target.value);
  };

  // Handle editor value change
  const handleEditorChange = (newValue) => {
    onChange(newValue);
  };

  // Handle editor mount - set up paste detection
  const handleEditorDidMount = (editor, monaco) => {
    // Add paste detection using Monaco's onDidPaste event
    editor.onDidPaste((e) => {
      // Get the pasted text from the range that was pasted
      const model = editor.getModel();
      if (model && e.range) {
        const pastedText = model.getValueInRange(e.range);
        
        // Log paste event using global function
        if (window.logCheatingEvent) {
          window.logCheatingEvent({
            type: 'paste-attempt',
            content: pastedText
          });
        }
      }
    });
  };

  // Handle escape key to exit full screen mode
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-90 z-50 flex">
      {/* Left panel - Question */}
      <div className="w-1/3 h-full overflow-auto p-6 bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-gray-800">
            Question {question.id}
            {/* <span className="ml-3 text-sm font-normal text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
              {question.type === 'code-completion' && 'Code Completion'}
              {question.type === 'code-refactoring' && 'Code Refactoring'}
              {question.type === 'code-write' && 'Code Writing'}
            </span> */}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-sm bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1 rounded-full flex items-center transition-colors duration-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Exit Full Screen
          </button>
        </div>
        
        <p className="text-lg mb-4 text-gray-700 font-medium">{question.questionText}</p>
        
        {/* Display skills tested if available */}
        {question.skillsTested && question.skillsTested.length > 0 && (
          <div className="mb-3">
            <span className="text-sm text-gray-500">Assessing: </span>
            {question.skillsTested.map((skill, index) => (
              <span key={index} className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded mr-1 mb-1">
                {skill}
              </span>
            ))}
          </div>
        )}
        
        {/* Display code snippet if available */}
        {question.codeSnippet && (
          <div className="mb-4">
            <CodeSnippetViewer 
              code={question.codeSnippet}
              language={snippetLanguage}
            />
          </div>
        )}
      </div>
      
      {/* Right panel - Code Editor */}
      <div className="w-2/3 h-full p-6 bg-gray-800 flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center">
            <span className="text-sm text-white mr-2">Your code solution:</span>
            <select
              value={language}
              onChange={handleLanguageChange}
              className="text-xs bg-blue-600 text-white px-2 py-1 rounded border-none focus:ring-2 focus:ring-blue-300"
              disabled={disabled}
            >
              {Object.entries(languageDisplayNames).map(([langKey, langName]) => (
                <option key={langKey} value={langKey}>
                  {langName}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="flex-1 min-h-0">
          <Editor
            height="100%"
            defaultLanguage={language}
            language={language}
            value={value}
            onChange={handleEditorChange}
            onMount={handleEditorDidMount}
            theme="vs-dark"
            options={{
              minimap: { enabled: true },
              scrollBeyondLastLine: true,
              fontSize: 14,
              fontFamily: '"Fira code", "Fira Mono", monospace',
              automaticLayout: true,
              tabSize: 2,
              wordWrap: 'on',
              lineNumbers: 'on',
              folding: true,
              renderLineHighlight: 'all',
              readOnly: disabled,
              fixedOverflowWidgets: true, // Ensures widgets like autocomplete don't get cut off
              suggest: {
                preview: true, // Show preview of the selected suggestion
                showStatusBar: true, // Show additional info at the bottom of the suggestion widget
                maxVisibleSuggestions: 12, // Limit number of visible suggestions
                filterGraceful: true, // Improves filtering
                showIcons: true, // Show icons in suggestions
                showMethods: true, // Show method suggestions
                showFunctions: true, // Show function suggestions
                showConstructors: true, // Show constructor suggestions
                showFields: true, // Show field suggestions
                showVariables: true, // Show variable suggestions
                showClasses: true, // Show class suggestions
                showStructs: true, // Show struct suggestions
                showInterfaces: true, // Show interface suggestions
                showModules: true, // Show module suggestions
                showProperties: true, // Show property suggestions
                showEvents: true, // Show event suggestions
                showOperators: true, // Show operator suggestions
                showUnits: true, // Show unit suggestions
                showValues: true, // Show value suggestions
                showConstants: true, // Show constant suggestions
                showEnums: true, // Show enum suggestions
                showEnumMembers: true, // Show enum member suggestions
                showKeywords: true, // Show keyword suggestions
                showWords: true, // Show word suggestions
                showColors: true, // Show color suggestions
                showFiles: true, // Show file suggestions
                showReferences: true, // Show reference suggestions
                showFolders: true, // Show folder suggestions
                showTypeParameters: true, // Show type parameter suggestions
                showSnippets: true, // Show snippet suggestions
              },
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default FullScreenCodeEditor;
